import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Spinner } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh } from '@fortawesome/free-solid-svg-icons';
import BZAPIService from '../../services/BZAPIService';
import { PageHead, PredictCard } from './PredictAtoms';
import { PROMOTION_SCORE, formatDateTime, formatScore } from './predictWatchlistModel';
import { WATCH_PATH } from './predictDashboardModel';
import {
  DETAIL_LIMIT,
  MAX_SCORE,
  binLabel,
  listCarriesDetails,
  runLimited,
  scoreHistogram,
  scorerVersions,
  weaknessBreakdown
} from './predictScoresModel';

const small = { fontSize: 'var(--fs-sm)' };

// Bug details read at once.
const DETAIL_CONCURRENCY = 5;

const isScore = score => typeof score === 'number' && Number.isFinite(score);

// The chart's frame, in SVG units; it scales to its card.
const W = 640;
const H = 190;
const LEFT = 34;
const TOP = 34;
const BOTTOM = 34;

const Histogram = ({ bins, threshold }) => {
  const start = bins[0].from;
  const max = Math.max(1, ...bins.map(bin => bin.count));
  const bw = W / bins.length;
  const x = score => LEFT + ((score - start) / (MAX_SCORE - start)) * W;
  const y = count => TOP + H - (count / max) * H;
  const sameMark = isScore(threshold) && threshold === PROMOTION_SCORE;
  const marks = [
    {
      kind: 'promoted',
      at: PROMOTION_SCORE,
      label: sameMark ? `${formatScore(PROMOTION_SCORE)} promoted · yours` : `${formatScore(PROMOTION_SCORE)} promoted`
    },
    ...(isScore(threshold) && !sameMark
      ? [{ kind: 'yours', at: threshold, label: `Yours ${formatScore(threshold)}` }]
      : [])
  ];
  const summary = bins
    .filter(bin => bin.count > 0)
    .map(bin => `${binLabel(bin, bin === bins[bins.length - 1])}: ${bin.count}`)
    .join('; ');

  return (
    <svg
      className="pdp-hist"
      viewBox={`0 0 ${LEFT + W + 12} ${TOP + H + BOTTOM}`}
      role="img"
      aria-label={`Scores on your watch list, in half-point bins. ${summary}. Bugs are promoted at ${formatScore(
        PROMOTION_SCORE
      )}${isScore(threshold) ? `; your threshold is ${formatScore(threshold)}` : ''}.`}
      data-testid="histogram"
    >
      {[0, 0.5, 1].map(f => (
        <g key={f}>
          <line className="pdp-hist__grid" x1={LEFT} x2={LEFT + W} y1={TOP + H - f * H} y2={TOP + H - f * H} />
          <text className="pdp-hist__text" x={LEFT - 6} y={TOP + H - f * H + 4} textAnchor="end">
            {Math.round(f * max)}
          </text>
        </g>
      ))}
      {bins.map((bin, k) => {
        const last = k === bins.length - 1;
        const h = TOP + H - y(bin.count);
        return (
          <g key={bin.from} data-testid="histogram-bin" data-from={bin.from} data-count={bin.count}>
            <rect
              className={`pdp-hist__bar${bin.from >= PROMOTION_SCORE ? ' pdp-hist__bar--promoted' : ''}`}
              x={LEFT + k * bw + 2}
              y={y(bin.count)}
              width={Math.max(1, bw - 4)}
              height={h}
              rx="2"
            >
              <title>{`${binLabel(bin, last)}: ${bin.count} ${bin.count === 1 ? 'bug' : 'bugs'}`}</title>
            </rect>
            {bin.count > 0 && (
              <text className="pdp-hist__count" x={LEFT + k * bw + bw / 2} y={y(bin.count) - 4} textAnchor="middle">
                {bin.count}
              </text>
            )}
            <text className="pdp-hist__text" x={LEFT + k * bw + bw / 2} y={TOP + H + 16} textAnchor="middle">
              {bin.from.toFixed(1)}
            </text>
          </g>
        );
      })}
      {marks.map((mark, i) => {
        const mx = x(mark.at);
        const end = mx > LEFT + W * 0.75;
        return (
          <g key={mark.kind}>
            <line
              className={`pdp-hist__mark pdp-hist__mark--${sameMark ? 'yours' : mark.kind}`}
              x1={mx}
              x2={mx}
              y1={TOP - 22 + i * 14}
              y2={TOP + H}
            />
            <text
              className={`pdp-hist__mark-label${
                mark.kind === 'yours' || sameMark ? ' pdp-hist__mark-label--yours' : ''
              }`}
              x={end ? mx - 5 : mx + 5}
              y={TOP - 14 + i * 14}
              textAnchor={end ? 'end' : 'start'}
            >
              {mark.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
};
Histogram.propTypes = {
  bins: PropTypes.arrayOf(
    PropTypes.shape({ from: PropTypes.number.isRequired, to: PropTypes.number.isRequired, count: PropTypes.number })
  ).isRequired,
  threshold: PropTypes.number
};

const Breakdown = ({ rows, total, testId }) => (
  <div className="pdp-breakdown" data-testid={testId}>
    {rows.map(row => (
      <React.Fragment key={row.key || '_none'}>
        <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{row.label}</span>
        <span className="bz-bar" aria-hidden="true">
          <span style={{ width: `${total ? (row.count / total) * 100 : 0}%`, background: 'var(--predict)' }} />
        </span>
        <span className="bz-mono" style={{ textAlign: 'right' }}>
          {row.count}
        </span>
      </React.Fragment>
    ))}
  </div>
);
Breakdown.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({ key: PropTypes.string, label: PropTypes.string.isRequired, count: PropTypes.number.isRequired })
  ).isRequired,
  total: PropTypes.number.isRequired,
  testId: PropTypes.string
};

const DetailProgress = ({ details, listed }) => {
  // Counted from the list itself: the cards add up to it, nothing to explain.
  if (details.status === 'idle' || details.fromList) return null;
  const capped = listed > DETAIL_LIMIT;
  return (
    <div className="bz-muted" style={{ ...small, marginTop: 10 }} data-testid="detail-progress">
      {details.status === 'loading'
        ? `Reading bug details… ${details.done} of ${details.total}`
        : capped
        ? `From the details of the ${details.total} highest-scoring of your ${listed} bugs.`
        : `From the details of ${details.total} ${details.total === 1 ? 'bug' : 'bugs'} on your watch list.`}
      {details.failed > 0 && ` ${details.failed} couldn't be read.`}
    </div>
  );
};
DetailProgress.propTypes = {
  details: PropTypes.shape({
    status: PropTypes.string.isRequired,
    done: PropTypes.number,
    total: PropTypes.number,
    ok: PropTypes.array,
    failed: PropTypes.number,
    fromList: PropTypes.bool
  }).isRequired,
  listed: PropTypes.number.isRequired
};

const NO_DETAILS = { status: 'idle', done: 0, total: 0, ok: [], failed: 0 };

/**
 * Predict → Model and scoring (STRY0039090).
 *
 * How the scores on the customer's watch list are spread: a histogram of the
 * same GET /predict/watchlist the watch list shows, so its bars add up to the
 * list, with 6.0 (where every bug is promoted) and the customer's threshold
 * marked. It is the customer's list, not every bug Predict scored, and says so.
 * The weakness types and scorer versions are counted over the whole list from
 * its own rows (STRY0039096), so both cards add up to it with no detail reads.
 * When the rows don't carry them (an ODD stage without that story), they come
 * from GET /predict/bug for the list's 25 highest scores (DETAIL_LIMIT), read a
 * few at a time after the list; the page then says when that is not the whole
 * list. Only what the data says about the scorer is shown.
 *
 * Real data only; the route sits behind <ProductGuard product="predict">.
 */
const PredictModel = () => {
  const [list, setList] = useState({ status: 'loading', bugs: [], threshold: null, asOf: null });
  const [details, setDetails] = useState(NO_DETAILS);
  const loadId = useRef(0);

  // A Refresh started since wins, and stops the reads of the one before.
  const load = useCallback(() => {
    const id = ++loadId.current;
    const current = () => id === loadId.current;
    setList(state => ({ ...state, status: 'loading' }));
    setDetails(NO_DETAILS);

    BZAPIService.getPredictWatchlist()
      .then(body => {
        if (!current()) return;
        const bugs = Array.isArray(body?.bugs) ? body.bugs.filter(b => b && b.vendor && b.bugId) : [];
        setList({
          status: 'ready',
          bugs,
          threshold: typeof body?.threshold === 'number' ? body.threshold : null,
          asOf: body?.asOf || null
        });
        if (listCarriesDetails(bugs)) {
          setDetails({ status: 'ready', done: bugs.length, total: bugs.length, ok: bugs, failed: 0, fromList: true });
          return;
        }
        // The API sends the list highest score first.
        const toRead = bugs.slice(0, DETAIL_LIMIT);
        if (!toRead.length) return;
        setDetails({ ...NO_DETAILS, status: 'loading', total: toRead.length });
        runLimited(toRead, DETAIL_CONCURRENCY, bug => BZAPIService.getPredictBug(bug.vendor, bug.bugId), {
          isCancelled: () => !current(),
          onSettled: done => {
            if (current()) setDetails(state => ({ ...state, done }));
          }
        }).then(results => {
          if (!current()) return;
          const ok = results.filter(r => r.ok && r.value?.bug).map(r => r.value.bug);
          setDetails({
            status: 'ready',
            done: results.length,
            total: toRead.length,
            ok,
            failed: results.length - ok.length
          });
        });
      })
      .catch(error => {
        console.error('Failed to load the Predict watch list:', error);
        if (current()) setList({ status: 'failed', bugs: [], threshold: null, asOf: null });
      });
  }, []);

  useEffect(() => {
    load();
    return () => {
      loadId.current += 1;
    };
  }, [load]);

  const histogram = useMemo(() => scoreHistogram(list.bugs, list.threshold), [list.bugs, list.threshold]);
  const weakness = useMemo(() => weaknessBreakdown(details.ok), [details.ok]);
  const versions = useMemo(() => scorerVersions(details.ok), [details.ok]);
  const isLoading = list.status === 'loading' || details.status === 'loading';
  const listed = list.bugs.length;

  return (
    <div className="pdp-page">
      <PageHead
        kicker="Predict · Model and scoring"
        title="Model and scoring"
        sub="How the scores on your watch list are spread, and which scorer scored them."
      >
        <button type="button" className="bz-btn bz-btn--sm" onClick={load} disabled={isLoading}>
          <FontAwesomeIcon icon={faRefresh} className={isLoading ? 'fa-spin' : ''} />
          {' Refresh'}
        </button>
      </PageHead>

      {list.status === 'loading' && list.bugs.length === 0 ? (
        <div className="text-center" style={{ padding: 40 }} role="status">
          <Spinner color="primary" />
          <div className="bz-muted" style={{ marginTop: 8 }}>
            Loading the watch list…
          </div>
        </div>
      ) : list.status === 'failed' ? (
        <div className="pl-empty">
          <div className="pl-empty__title">Predict data is unavailable right now.</div>
          <div className="bz-muted">Try again in a minute.</div>
          <button type="button" className="bz-btn bz-btn--sm" onClick={load}>
            Try again
          </button>
        </div>
      ) : (
        <div className="pdp-split">
          <PredictCard
            title="Scores on your watch list"
            labelledBy="pdp-hist"
            right={
              <span className="bz-muted" style={small} data-testid="hist-total">
                {listed} {listed === 1 ? 'bug' : 'bugs'}
                {list.asOf ? ` · updated ${formatDateTime(list.asOf)}` : ''}
              </span>
            }
          >
            <div className="pdp-card-body">
              {listed === 0 ? (
                <div className="pdp-empty" data-testid="hist-empty">
                  <b>No bugs on your watch list.</b>
                  No bug in your scope scores {formatScore(list.threshold)} or higher yet.
                </div>
              ) : (
                <>
                  <Histogram bins={histogram.bins} threshold={list.threshold} />
                  <div className="bz-muted" style={{ ...small, marginTop: 8 }}>
                    Each bar counts the bugs on <Link to={WATCH_PATH}>your watch list</Link> scoring in that half point;
                    together they are all {histogram.scored} of its scored bugs.
                    {histogram.unscored > 0 && ` ${histogram.unscored} more have no score.`} Scores are from 0.0 to
                    10.0. This is your list only, not every bug Predict scores.
                  </div>
                </>
              )}
            </div>
          </PredictCard>

          <div className="pdp-rail">
            <PredictCard title="Weakness types" labelledBy="pdp-weakness">
              <div className="pdp-card-body">
                {details.status === 'ready' && weakness.length > 0 ? (
                  <Breakdown rows={weakness} total={details.ok.length} testId="weakness-breakdown" />
                ) : details.status === 'loading' ? (
                  <Spinner color="primary" size="sm" />
                ) : (
                  <span className="bz-muted">None to show.</span>
                )}
                <div className="bz-muted" style={{ ...small, marginTop: 10 }}>
                  The scorer&apos;s defect class for each bug.
                </div>
                <DetailProgress details={details} listed={listed} />
              </div>
            </PredictCard>
            <PredictCard title="Scorer versions" labelledBy="pdp-versions">
              <div className="pdp-card-body">
                {details.status === 'ready' && versions.length > 0 ? (
                  <Breakdown rows={versions} total={details.ok.length} testId="scorer-versions" />
                ) : details.status === 'loading' ? (
                  <Spinner color="primary" size="sm" />
                ) : (
                  <span className="bz-muted">None to show.</span>
                )}
                <div className="bz-muted" style={{ ...small, marginTop: 10 }}>
                  The version of Predict&apos;s scorer that gave each bug the score it keeps.
                </div>
              </div>
            </PredictCard>
          </div>
        </div>
      )}
    </div>
  );
};

export default PredictModel;
